import { ChangeDetectionStrategy, Component, effect, inject, input, output, signal } from '@angular/core';
import { ClubOption, ClubService } from '../../services/club.service';
import { TranslatePipe } from '../../pipes/translate.pipe';

/**
 * A typed club name resolved to a club id. `resolved` is false while the text matches no known
 * club: the parent must not save it, since humans only reference clubs by id.
 */
export interface ClubSelection {
  id: string | null;
  name: string;
  resolved: boolean;
}

let nextPickerId = 0;

@Component({
  selector: 'app-club-picker',
  standalone: true,
  imports: [TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <input
      type="text"
      autocomplete="off"
      [class]="inputClass()"
      [attr.list]="listId"
      [attr.aria-label]="ariaLabel() || null"
      [attr.aria-invalid]="unknown() || null"
      [placeholder]="'clubPicker.placeholder' | translate"
      [value]="text()"
      (input)="onInput($any($event.target).value)"
      (change)="resolve($any($event.target).value)"
    />
    <datalist [id]="listId">
      @for (option of options(); track option.id) {
        <option [value]="option.name"></option>
      }
    </datalist>
    @if (unknown()) {
      <p class="mt-1 text-xs font-bold text-red-600 dark:text-red-400">{{ 'clubPicker.unknown' | translate }}</p>
    }
  `,
})
export class ClubPickerComponent {
  private readonly clubService = inject(ClubService);

  /** Current club name, shown when the picker opens. */
  readonly clubName = input<string | null | undefined>('');
  readonly inputClass = input('');
  readonly ariaLabel = input('');
  readonly clubChange = output<ClubSelection>();

  readonly listId = `club-picker-${nextPickerId++}`;
  readonly text = signal('');
  readonly options = signal<ClubOption[]>([]);
  readonly unknown = signal(false);
  private searchTimer: ReturnType<typeof setTimeout> | null = null;
  private searchVersion = 0;

  constructor() {
    effect(() => {
      this.text.set(this.clubName() ?? '');
      this.unknown.set(false);
    });
  }

  onInput(value: string): void {
    this.text.set(value);
    this.unknown.set(false);
    if (this.searchTimer) clearTimeout(this.searchTimer);
    const version = ++this.searchVersion;
    this.searchTimer = setTimeout(async () => {
      try {
        const options = await this.clubService.searchClubs(value);
        if (version === this.searchVersion) this.options.set(options);
      } catch (error) {
        console.error('Club search failed:', error);
      }
    }, 250);
  }

  async resolve(value: string): Promise<void> {
    const name = value.trim();
    this.text.set(value);
    if (!name) {
      this.unknown.set(false);
      this.clubChange.emit({ id: null, name: '', resolved: true });
      return;
    }
    const lower = name.toLocaleLowerCase('fr');
    let match = this.options().find(option => option.name.trim().toLocaleLowerCase('fr') === lower) ?? null;
    if (!match) {
      try {
        match = await this.clubService.findClubByName(name);
      } catch (error) {
        console.error('Club lookup failed:', error);
      }
    }
    this.unknown.set(!match);
    this.clubChange.emit(match
      ? { id: match.id, name: match.name, resolved: true }
      : { id: null, name, resolved: false });
  }
}
